/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable import/no-cycle */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import ClearIcon from '@mui/icons-material/Clear';
import React, { useContext, useRef } from 'react';
import styled, { ThemeContext } from 'styled-components/macro';

import { KeyHandler } from '../components/KeyHandler';
import { SVGIconButton } from '../components/SVGIconButton';
import { useOutsideCallback } from '../utils/customHooks';

const ModalBackground = styled.div`
  /* Fullscreen partly-transparent overlay */
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  /* Have Modal be at the foreground */
  z-index: var(--z-modal);

  /* Make the modal centered */
  display: grid;
  place-items: center;
`;

// Not much styling here, whoever uses the Modal component should
// augment this to implement their own styling
const ModalContainer = styled.div`
  background-color: ${(props) => props.theme.bgColor};
  border-radius: 16px;
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.2);

  /* Allow some elements, like the Close button to be positioned
  relative to this modal */
  position: relative;
`;

// Container for the 'X' icon on the top right of the modal
const CloseModalContainer = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;

  @media (max-width: 1280px) {
    button {
      width: 24px;
      min-width: 24px;
      height: 24px;
      min-height: 24px;
    }
  }
`;

const Modal: React.FC<{
  onClose: () => void;
  // Needed so that styled-components can style this Button
  // https://styled-components.com/docs/basics#styling-any-component
  closeOnClickOutside?: boolean;
  className?: string;
}> = ({ onClose, closeOnClickOutside = true, children, className }) => {
  // Get theme to access icon color
  const themeContext = useContext(ThemeContext);

  // Close modal when clicked outside of it
  const modalRef = useRef<HTMLDivElement>(null);
  useOutsideCallback(modalRef, onClose, closeOnClickOutside);

  return (
    <ModalContainer className={className} ref={modalRef}>
      <KeyHandler
        keyDownHandler={(ev) => {
          if ((ev as KeyboardEvent).key === 'Escape') {
            onClose();
          }
        }}
      />
      <CloseModalContainer>
        <SVGIconButton
          size="36px"
          color={themeContext.textDefaultColor}
          onClick={() => {
            onClose();
          }}
        >
          <ClearIcon />
        </SVGIconButton>
      </CloseModalContainer>
      {children}
    </ModalContainer>
  );
};

// ModalBackground is exported as a separate styled component from the
// Modal component because whoever adds a modal might want to override
// its background display properties. Most users will just want to wrap
// Modal into ModalBackground
export { Modal, ModalBackground };
